import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import notify from '../../utils/IANotification';
import Switch from 'react-switch'
import { MdAdd, MdContentCopy, MdDelete, MdEdit, MdEmail, MdFacebook, MdGroups2, MdLocationOn, MdPhone, MdPolicy, MdSave, MdTimer, MdWeb } from 'react-icons/md'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import AdminSettingsNavbar from '../../components/AdminSettingsNavbar';
import FileUploadField from '../../components/FileUploadField';
import { IoLogoInstagram } from 'react-icons/io5';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { fetch_all_users_admin } from '../../api/usersAPI';

export default function SettingsPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  const tempState = new URLSearchParams(window.location.search).get("state")
  const [state, setState] = useState(tempState ? tempState : "general")
  const [usersList, setUsersList] = useState([])
  const [newReportTypeInput, setNewReportTypeInput] = useState("")

  const [generalSettings, setGeneralSettings] = useState({
    session_timeout: 0,
    max_login_attempts: 0,
    allow_login_student: "0",
    allow_login_parent: "0",
    allow_login_staff: "0",
    show_announcement_banner: "0",
    announcement_banner: "",
    contact_forward_email: "",
    library_loan_period: "",
    library_loan_limit: "",
    library_renewal_limit: "",
    is_booking_auto_approved: "0",
    booking_approver_user_ids: [],
    maintenance_report_types: ["Aircon", "Electrical", "Plumbing", "Furniture", "Cleanliness", "Audio/Visual", "Other"],
  })

  const SERVICE_DAYS = [
    { value: "0", label: "Sunday" },
    { value: "1", label: "Monday" },
    { value: "2", label: "Tuesday" },
    { value: "3", label: "Wednesday" },
    { value: "4", label: "Thursday" },
    { value: "5", label: "Friday" },
    { value: "6", label: "Saturday" },
  ]

  const [policySettings, setPolicySettings] = useState([])
  const [policyData, setPolicyData] = useState("")
  const [contactSettings, setContactSettings] = useState([])
  const [serviceSettings, setServiceSettings] = useState([])

  const LINK_TYPE = [
    { type: "email", name: "Email Address", icon: <MdEmail size={20} /> },
    { type: "phone", name: "Contact Number", icon: <MdPhone size={20} /> },
    { type: "website", name: "Website links", icon: <MdWeb size={20} /> },
    { type: "address", name: "Address", icon: <MdLocationOn size={20} /> },
    { type: "facebook", name: "Facebook", icon: <MdFacebook size={20} /> },
    { type: "instagram", name: "Instagram", icon: <IoLogoInstagram size={20} /> },
  ]

  const saveGeneralSettings = async () => {
    let endpoint = config.endpoint + "/config/general"
    let fetchResponse = await fetch(endpoint, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...generalSettings,
        session_timeout: parseInt(generalSettings.session_timeout || 0),
        max_login_attempts: parseInt(generalSettings.max_login_attempts || 0),
      }),
      credentials: "include",
    })

    let data = await fetchResponse.json()
    if (data.status === 200) {
      notify({ message: "General settings saved successfully", type: "success" })
    } else {
      notify({ message: "An error occurred while saving general settings", type: "error" })
    }
  }

  const savePolicySettings = async () => {
    let endpoint = config.endpoint + "/config/policy"
    let fetchResponse = await fetch(endpoint, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ policy: policySettings }),
      credentials: "include",
    })

    let data = await fetchResponse.json()
    if (data.status === 200) {
      notify({ message: "Policy settings saved successfully", type: "success" })
    } else {
      notify({ message: "An error occurred while saving policy settings", type: "error" })
    }
  }

  const saveServiceSettings = async () => {
    let endpoint = config.endpoint + "/config/services"
    let fetchResponse = await fetch(endpoint, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ service: serviceSettings }),
      credentials: "include",
    })

    let data = await fetchResponse.json()
    if (data.status === 200) {
      fetchConfigSettings()
      notify({ message: "Service profile settings saved", type: "success" })
    } else {
      notify({ message: "An error occurred while saving services", type: "error" })
    }
  }

  const saveContactSettings = async () => {
    let endpoint = config.endpoint + "/config/contact"
    let fetchResponse = await fetch(endpoint, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contact: contactSettings }),
      credentials: "include",
    })

    let data = await fetchResponse.json()
    if (data.status === 200) {
      notify({ message: "Contact information saved", type: "success" })
    } else {
      notify({ message: "An error occurred while saving contact info", type: "error" })
    }
  }

  const fetchConfigSettings = async () => {
    let endpoint = config.endpoint + "/config"
    let fetchResponse = await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })

    let data = await fetchResponse.json()
    if (data.status === 200) {
      setGeneralSettings({
        is_booking_auto_approved: "0",
        booking_approver_user_ids: [],
        maintenance_report_types: ["Aircon", "Electrical", "Plumbing", "Furniture", "Cleanliness", "Audio/Visual", "Other"],
        ...(data?.data?.general || {}),
      })
      setPolicySettings([...(data?.data?.policy || [])])
      let links = data?.data?.links || []
      links.forEach(link => { link.is_editing = "0" })
      setContactSettings(links)
      let services = data?.data?.services || []
      services.forEach(service => { service.is_editing = "0" })
      setServiceSettings(services)
    } else {
      notify({ message: "An error occurred fetching settings", type: "error" })
    }
  }

  useEffect(() => {
    fetchConfigSettings()
    const loadUsers = async () => {
      const res = await fetch_all_users_admin()
      if (res?.status === 200) {
        setUsersList(res.data || [])
      }
    }
    loadUsers()
  }, [])

  return (
    <AdminLayout navSelected={"Settings"} headerText="System & Church Settings">
      <AdminSettingsNavbar selected={state} setState={setState} />

      {/* GENERAL SETTINGS TAB */}
      {state === "general" && (
        <div className="space-y-6">
          <Card title="Feedback &amp; External Links" subtitle="Configure public feedback forms and links.">
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Allow Public Feedback</label>
                  <p className="text-xs text-gray-500">Enable or disable user feedback forms on mobile and web.</p>
                </div>
                <Switch
                  checked={generalSettings.allow_feedback === "1"}
                  onChange={checked => setGeneralSettings({ ...generalSettings, allow_feedback: checked ? "1" : "0" })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Feedback Form Link</label>
                <input
                  type="text"
                  className="text-input"
                  value={generalSettings.feedback_link || ""}
                  placeholder='e.g. https://forms.gle/1234567890'
                  onChange={e => setGeneralSettings({ ...generalSettings, feedback_link: e.target.value })}
                />
              </div>
            </div>
          </Card>

          <Card title="Mobile Application Links &amp; Version Control" subtitle="Configure App Store links and allowed client versions.">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="input-container">
                  <label className="input-label font-semibold">Apple App Store (iOS) URL</label>
                  <input
                    type="text"
                    className="text-input"
                    value={generalSettings.ios_link || ""}
                    placeholder='https://apps.apple.com/us/app/bmc/id1234567890'
                    onChange={e => setGeneralSettings({ ...generalSettings, ios_link: e.target.value })}
                  />
                </div>

                <div className="input-container">
                  <label className="input-label font-semibold">Google Play Store (Android) URL</label>
                  <input
                    type="text"
                    className="text-input"
                    value={generalSettings.android_link || ""}
                    placeholder='https://play.google.com/store/apps/details?id=com.bmc'
                    onChange={e => setGeneralSettings({ ...generalSettings, android_link: e.target.value })}
                  />
                </div>
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Allowed App Versions</label>
                <CreatableSelect
                  isMulti
                  placeholder="e.g. 1.2.1, 1.2.0, 1.1.9"
                  value={(generalSettings.allowed_app_versions || []).map(v => ({ value: v, label: v }))}
                  onChange={selected => {
                    const versions = (selected || []).map(s => s.value)
                    setGeneralSettings({ ...generalSettings, allowed_app_versions: versions })
                  }}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Users on versions omitted from this list will be prompted to update. Leave empty to allow all versions.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Church Library &amp; Contact Inquiries" subtitle="Loan durations, limits, and email routing.">
            <div className="space-y-6">
              <div className="input-container">
                <label className="input-label font-semibold">Contact Form Forward Email</label>
                <input
                  type="email"
                  className="text-input"
                  value={generalSettings.contact_forward_email || ""}
                  placeholder='e.g. hello@bmc.org.sg'
                  onChange={e => setGeneralSettings({ ...generalSettings, contact_forward_email: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="input-container">
                  <label className="input-label font-semibold">Library Loan Period (Days)</label>
                  <input
                    type="text"
                    className="text-input"
                    value={generalSettings.library_loan_period || ""}
                    placeholder='e.g. 14'
                    onChange={e => setGeneralSettings({ ...generalSettings, library_loan_period: e.target.value })}
                  />
                </div>
                <div className="input-container">
                  <label className="input-label font-semibold">Library Item Loan Limit</label>
                  <input
                    type="text"
                    className="text-input"
                    value={generalSettings.library_loan_limit || ""}
                    placeholder='e.g. 5'
                    onChange={e => setGeneralSettings({ ...generalSettings, library_loan_limit: e.target.value })}
                  />
                </div>
                <div className="input-container">
                  <label className="input-label font-semibold">Library Renewal Limit</label>
                  <input
                    type="text"
                    className="text-input"
                    value={generalSettings.library_renewal_limit || ""}
                    placeholder='e.g. 2'
                    onChange={e => setGeneralSettings({ ...generalSettings, library_renewal_limit: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card title="Global Announcement Ticker Banner" subtitle="Display a highlighted announcement banner across member pages.">
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Show Announcement Ticker</label>
                  <p className="text-xs text-gray-500">Enable banner ticker across website and mobile header.</p>
                </div>
                <Switch
                  checked={generalSettings.show_announcement_banner === "1"}
                  onChange={checked => setGeneralSettings({ ...generalSettings, show_announcement_banner: checked ? "1" : "0" })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Banner Message Content</label>
                <textarea
                  className="textarea-input h-24"
                  value={generalSettings.announcement_banner || ""}
                  placeholder='e.g. Welcome to Bedok Methodist Church! Sunday services start at 8:30 AM.'
                  onChange={e => setGeneralSettings({ ...generalSettings, announcement_banner: e.target.value })}
                />
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-100">
                <Button variant="primary" icon={MdSave} onClick={saveGeneralSettings}>
                  Save General Settings
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* FACILITIES & BOOKING TAB */}
      {state === "facilities" && (
        <div className="space-y-6">
          <Card title="Facility Booking &amp; Approval Configuration" subtitle="Set auto-approval rules and select designated booking approvers.">
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Auto-Approve Facility Bookings</label>
                  <p className="text-xs text-gray-500">When enabled, newly created facility bookings are automatically approved upon submission.</p>
                </div>
                <Switch
                  checked={generalSettings.is_booking_auto_approved === "1"}
                  onChange={checked => setGeneralSettings({ ...generalSettings, is_booking_auto_approved: checked ? "1" : "0" })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Designated Booking Approver Users</label>
                <p className="text-xs text-gray-500 mb-2">Select staff or managers authorized to approve or reject pending facility bookings.</p>
                <Select
                  isMulti
                  options={usersList.map(u => ({
                    value: u.id,
                    label: `${u.first_name} ${u.last_name} (${u.email || u.account_type})`
                  }))}
                  value={usersList.map(u => ({
                    value: u.id,
                    label: `${u.first_name} ${u.last_name} (${u.email || u.account_type})`
                  })).filter(opt => (generalSettings.booking_approver_user_ids || []).includes(opt.value))}
                  onChange={selected => setGeneralSettings({
                    ...generalSettings,
                    booking_approver_user_ids: selected ? selected.map(item => item.value) : []
                  })}
                  className="text-sm"
                />
              </div>
            </div>
          </Card>

          <Card title="Dynamic Maintenance Issue Categories" subtitle="Manage report types selectable when users submit maintenance reports.">
            <div className="space-y-6">
              <div>
                <label className="input-label font-semibold">Configured Report Types</label>
                <div className="flex flex-wrap gap-2 my-3">
                  {(generalSettings.maintenance_report_types || []).map((type, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-800 rounded-lg text-xs font-bold border border-blue-200">
                      {type}
                      <button
                        type="button"
                        className="text-blue-600 hover:text-red-600 font-bold"
                        onClick={() => {
                          const updated = (generalSettings.maintenance_report_types || []).filter(t => t !== type);
                          setGeneralSettings({ ...generalSettings, maintenance_report_types: updated });
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 items-center">
                <div className="input-container flex-1 mb-0">
                  <label className="input-label font-semibold">Add New Maintenance Report Type</label>
                  <input
                    type="text"
                    className="text-input mb-0"
                    placeholder="e.g. Sound System, Plumbing, HVAC"
                    value={newReportTypeInput}
                    onChange={e => setNewReportTypeInput(e.target.value)}
                  />
                </div>
                <Button
                  variant="secondary"
                  icon={MdAdd}
                  onClick={() => {
                    if (!newReportTypeInput.trim()) return;
                    const currentTypes = generalSettings.maintenance_report_types || [];
                    if (currentTypes.includes(newReportTypeInput.trim())) return;
                    setGeneralSettings({
                      ...generalSettings,
                      maintenance_report_types: [...currentTypes, newReportTypeInput.trim()]
                    });
                    setNewReportTypeInput("");
                  }}
                >
                  Add Type
                </Button>
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-100">
                <Button variant="primary" icon={MdSave} onClick={saveGeneralSettings}>
                  Save Facilities Settings
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* POLICY TAB — moved to Website > Policies */}
      {state === "policy" && (
        <Card
          title="Church Policy Management"
          subtitle="Policy management has been moved to the Website section for better organisation."
        >
          <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <MdPolicy size={56} className="text-gray-300" />
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Policies have moved</h4>
              <p className="text-sm text-gray-500 max-w-sm">
                Manage church policies, governance guidelines, and data protection terms from the <strong>Website → Policies</strong> section.
              </p>
            </div>
            <Button
              variant="primary"
              icon={MdPolicy}
              onClick={() => navigate('/admin/website/policies')}
            >
              Go to Policies
            </Button>
          </div>
        </Card>
      )}

      {/* CONTACT TAB */}
      {state === "contact" && (
        <Card title="Church Contact Channels &amp; Links" subtitle="Manage public contact numbers, emails, addresses, and social links.">
          <div className="space-y-6">
            {LINK_TYPE.map((type) => (
              <div key={type.type} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-bold text-sm text-gray-900">
                    {type.icon}
                    <span>{type.name}</span>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={MdAdd}
                    onClick={() => {
                      let temp = [...contactSettings]
                      temp.forEach((c) => { c.is_editing = "0" })
                      temp.push({
                        link_type: type.type,
                        link_name: "New " + type.name,
                        link_url: "",
                        is_public: "1",
                        is_editing: "1",
                      })
                      setContactSettings(temp)
                    }}
                  >
                    Add {type.name}
                  </Button>
                </div>

                <div className="space-y-3">
                  {contactSettings
                    .map((contact, index) => ({ contact, index }))
                    .filter(({ contact }) => contact.link_type === type.type)
                    .map(({ contact, index }) => (
                      <div key={index} className="p-4 bg-white rounded-xl border border-gray-200">
                        {contact?.is_editing === "0" ? (
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-bold text-xs text-gray-900">{contact?.link_name}</p>
                              <p className="text-xs text-gray-500 truncate max-w-md">{contact?.link_url || "No link specified"}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              icon={MdEdit}
                              onClick={() => {
                                let temp = [...contactSettings]
                                temp.forEach((c) => { c.is_editing = "0" })
                                temp[index].is_editing = "1"
                                setContactSettings(temp)
                              }}
                            >
                              Edit
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="input-container">
                                <label className="input-label font-semibold">Label Name</label>
                                <input
                                  type="text"
                                  className="text-input"
                                  value={contact?.link_name || ""}
                                  placeholder="e.g. Main Church Office Phone"
                                  onChange={e => {
                                    let temp = [...contactSettings]
                                    temp[index].link_name = e.target.value
                                    setContactSettings(temp)
                                  }}
                                />
                              </div>

                              <div className="input-container">
                                <label className="input-label font-semibold">Contact URL / Value</label>
                                <input
                                  type="text"
                                  className="text-input"
                                  value={contact?.link_url || ""}
                                  placeholder="Target URL or value..."
                                  onChange={e => {
                                    let temp = [...contactSettings]
                                    temp[index].link_url = e.target.value
                                    setContactSettings(temp)
                                  }}
                                />
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-2">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={contact?.is_public === "1"}
                                  onChange={checked => {
                                    let temp = [...contactSettings]
                                    temp[index].is_public = checked ? "1" : "0"
                                    setContactSettings(temp)
                                  }}
                                />
                                <span className="text-xs font-semibold text-gray-700">Publicly Visible</span>
                              </div>

                              <div className="flex items-center gap-2">
                                <Button
                                  variant="danger"
                                  size="sm"
                                  icon={MdDelete}
                                  onClick={() => {
                                    let temp = contactSettings.filter((_, i) => i !== index)
                                    setContactSettings(temp)
                                  }}
                                >
                                  Delete
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  icon={MdSave}
                                  onClick={() => {
                                    let temp = [...contactSettings]
                                    temp[index].is_editing = "0"
                                    setContactSettings(temp)
                                    saveContactSettings()
                                  }}
                                >
                                  Done
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end pt-4 border-t border-gray-100">
              <Button variant="primary" icon={MdSave} onClick={saveContactSettings}>
                Save Contact Settings
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* SERVICE PROFILE TAB */}
      {state === "service" && (
        <Card
          title="Congregation Service Profiles"
          subtitle="Configure service types (e.g. Traditional, Contemporary, Youth) and default timings."
          headerRight={
            <Button
              variant="primary"
              icon={MdAdd}
              onClick={() => {
                let temp = [...serviceSettings]
                temp.push({
                  name: "New Service Profile",
                  description: "",
                  identifier: Math.random().toString(36).substring(7)
                })
                setServiceSettings(temp)
              }}
            >
              Add Service Profile
            </Button>
          }
        >
          <div className="space-y-4">
            {serviceSettings?.length > 0 ? (
              serviceSettings.map((service, index) => (
                <div key={index} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-sm text-gray-900">{service?.name}</h4>
                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1"><MdTimer size={14} /> {service?.datetime || "N/A"}</span>
                        <span className="flex items-center gap-1"><MdGroups2 size={14} /> Min Age: {service?.minimum_age || 0}+</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={MdContentCopy}
                        onClick={async () => {
                          if (!service?.id) {
                            notify({ message: "Save the profile first before duplicating", type: "info" })
                            return
                          }
                          let endpoint = config.endpoint + `/config/services/duplicate/id/${service.id}`
                          let fetchResponse = await fetch(endpoint, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                          })
                          let data = await fetchResponse.json()
                          if (data.status === 200) {
                            fetchConfigSettings()
                            notify({ message: "Service profile duplicated", type: "success" })
                          } else {
                            notify({ message: "Failed to duplicate profile", type: "error" })
                          }
                        }}
                      >
                        Duplicate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={MdEdit}
                        onClick={() => {
                          let temp = [...serviceSettings]
                          temp.forEach((s) => { s.is_editing = "0" })
                          temp[index].is_editing = "1"
                          setServiceSettings(temp)
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>

                  {service?.is_editing === "1" && (
                    <div className="p-4 bg-white rounded-xl border border-gray-200 space-y-4">
                      {service?.id && (
                        <FileUploadField
                          text="Upload Service Header Image"
                          upload_url={config.endpoint + "/file/services/type/id/" + service?.id}
                          subtext=""
                          max_file_size={"5"}
                          max_files={1}
                          onChange={() => fetchConfigSettings()}
                        />
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="input-container md:col-span-2">
                          <label className="input-label font-semibold">Service Profile Name</label>
                          <input
                            type="text"
                            className="text-input"
                            value={service?.name || ""}
                            placeholder='e.g. Traditional Service'
                            onChange={e => {
                              let temp = [...serviceSettings]
                              temp[index].name = e.target.value
                              setServiceSettings(temp)
                            }}
                          />
                        </div>
                        <div className="input-container">
                          <label className="input-label font-semibold">Minimum Age</label>
                          <input
                            type="number"
                            className="text-input"
                            value={service?.minimum_age || ""}
                            placeholder='e.g. 18'
                            onChange={e => {
                              let temp = [...serviceSettings]
                              temp[index].minimum_age = e.target.value
                              setServiceSettings(temp)
                            }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="input-container">
                          <label className="input-label font-semibold">Service Start Time</label>
                          <input
                            type="time"
                            className="text-input"
                            value={service?.datetime || ""}
                            onChange={e => {
                              let temp = [...serviceSettings]
                              temp[index].datetime = e.target.value
                              setServiceSettings(temp)
                            }}
                          />
                        </div>
                        <div className="input-container">
                          <label className="input-label font-semibold">Service Day</label>
                          <Select
                            options={SERVICE_DAYS}
                            value={SERVICE_DAYS.find(day => day.value === service?.day)}
                            onChange={e => {
                              let temp = [...serviceSettings]
                              temp[index].day = e.value
                              setServiceSettings(temp)
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-6 py-2">
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700">
                          <input
                            type="checkbox"
                            checked={service?.is_onsite === "1" || service?.is_onsite === 1 || service?.is_onsite === true}
                            onChange={e => {
                              let temp = [...serviceSettings]
                              temp[index].is_onsite = e.target.checked ? "1" : "0"
                              setServiceSettings(temp)
                            }}
                          />
                          On-Site Service
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700">
                          <input
                            type="checkbox"
                            checked={service?.is_online === "1" || service?.is_online === 1 || service?.is_online === true}
                            onChange={e => {
                              let temp = [...serviceSettings]
                              temp[index].is_online = e.target.checked ? "1" : "0"
                              setServiceSettings(temp)
                            }}
                          />
                          Online Service
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700">
                          <input
                            type="checkbox"
                            checked={service?.is_visible === "1" || service?.is_visible === 1 || service?.is_visible === true || service?.is_visible === undefined}
                            onChange={e => {
                              let temp = [...serviceSettings]
                              temp[index].is_visible = e.target.checked ? "1" : "0"
                              setServiceSettings(temp)
                            }}
                          />
                          Visible on Public Website
                        </label>
                      </div>

                      {(service?.is_online === "1" || service?.is_online === 1 || service?.is_online === true) && (
                        <div className="input-container">
                          <label className="input-label font-semibold">Live Stream URL</label>
                          <input
                            type="url"
                            className="text-input"
                            value={service?.online_url || ""}
                            placeholder="https://youtube.com/live/..."
                            onChange={e => {
                              let temp = [...serviceSettings]
                              temp[index].online_url = e.target.value
                              setServiceSettings(temp)
                            }}
                          />
                        </div>
                      )}

                      <div className="input-container">
                        <label className="input-label font-semibold">Description</label>
                        <textarea
                          className="textarea-input h-24"
                          value={service?.description || ""}
                          placeholder='e.g. Traditional service format with hymns and organ liturgy...'
                          onChange={e => {
                            let temp = [...serviceSettings]
                            temp[index].description = e.target.value
                            setServiceSettings(temp)
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={MdContentCopy}
                          onClick={async () => {
                            if (!service?.id) {
                              notify({ message: "Save the profile first before duplicating", type: "info" })
                              return
                            }
                            let endpoint = config.endpoint + `/config/services/duplicate/id/${service.id}`
                            let fetchResponse = await fetch(endpoint, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              credentials: "include",
                            })
                            let data = await fetchResponse.json()
                            if (data.status === 200) {
                              fetchConfigSettings()
                              notify({ message: "Service profile duplicated", type: "success" })
                            } else {
                              notify({ message: "Failed to duplicate profile", type: "error" })
                            }
                          }}
                        >
                          Duplicate
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          icon={MdDelete}
                          onClick={() => {
                            let temp = serviceSettings.filter((_, i) => i !== index)
                            setServiceSettings(temp)
                          }}
                        >
                          Delete
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={MdSave}
                          onClick={() => {
                            let temp = [...serviceSettings]
                            temp[index].is_editing = "0"
                            setServiceSettings(temp)
                            saveServiceSettings()
                          }}
                        >
                          Save Profile
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-400 italic">No service profiles configured.</p>
            )}

            <div className="flex justify-end pt-4 border-t border-gray-100">
              <Button variant="primary" icon={MdSave} onClick={saveServiceSettings}>
                Save Service Profile Settings
              </Button>
            </div>
          </div>
        </Card>
      )}
    </AdminLayout>
  )
}
